import { useSelector } from 'react-redux';
import {
  selectBonusSpendability,
  selectBonusSpendabilityLoadState,
} from 'services/bonus/bonus.selectors';

type BonusSummaryType = 'BS' | 'FB' | 'FUN' | 'FS' | 'FSG';

const BONUS_TYPE_CODES: Record<BonusSummaryType, string[]> = {
  BS: ['BS', '3'],
  FB: ['FB'],
  FUN: ['FUN'],
  FS: ['FS'],
  FSG: ['FSG'],
};

const BONUS_TYPE_DESCRIPTION_KEYWORDS: Record<BonusSummaryType, string[]> = {
  BS: ['BONUS STANDARD'],
  FB: ['FREEBET'],
  FUN: ['FUN'],
  FS: ['FREE SPIN', 'FREESPIN'],
  FSG: ['FREESPIN GOLD', 'FREE SPIN GOLD', 'GOLD'],
};

export function useBonusData() {
  const spendability = useSelector(selectBonusSpendability);

  const { isLoaded: spendabilityIsLoaded, hasFailed: spendabilityHasFailed } =
    useSelector(selectBonusSpendabilityLoadState);

  const hasSpendabilityLoaded =
    (spendabilityHasFailed || spendabilityIsLoaded) &&
    spendability.length === 0;

  const safeNullable = (value: number | undefined | null) =>
    typeof value === 'number' && !Number.isNaN(value) && value !== 0
      ? value
      : null;

  const getResidualAmount = (type: BonusSummaryType) => {
    const codes = BONUS_TYPE_CODES[type];
    const keywords = BONUS_TYPE_DESCRIPTION_KEYWORDS[type];

    const matchingBonuses = spendability.filter((bonus) => {
      const matchesCode = codes.includes(bonus.bonusTypeCode);
      if (matchesCode) return true;

      const normalizedDescription = (
        bonus.bonusTypeDescription ?? ''
      ).toUpperCase();
      if (!normalizedDescription) return false;

      if (type === 'FS' && normalizedDescription.includes('GOLD')) {
        return false;
      }

      return keywords.some((keyword) =>
        normalizedDescription.includes(keyword),
      );
    });

    if (matchingBonuses.length === 0) return 0;

    return matchingBonuses.reduce((total, bonus) => {
      const amount = Number(bonus.residualAmount ?? 0);
      if (!Number.isFinite(amount)) return total;
      return total + amount;
    }, 0);
  };

  const realResidual = getResidualAmount('BS');
  const funResidual = getResidualAmount('FUN');
  const freebetResidual = getResidualAmount('FB');
  const freespinResidual = getResidualAmount('FS');
  const freespinGoldResidual = getResidualAmount('FSG');

  const mappedRealBonus = safeNullable(realResidual);
  const mappedFunBonus = safeNullable(funResidual);
  const mappedFreebet = safeNullable(freebetResidual);
  const mappedFreespin = safeNullable(freespinResidual);
  const mappedFreespinGold = safeNullable(freespinGoldResidual);

  return {
    realBonus: mappedRealBonus,
    funBonus: mappedFunBonus,
    freebet: mappedFreebet,
    freespin: mappedFreespin,
    freespinGold: mappedFreespinGold,
    hasAnyBonus: Boolean(
      mappedRealBonus ||
        mappedFunBonus ||
        mappedFreebet ||
        mappedFreespin ||
        mappedFreespinGold,
    ),
    hasSpendabilityLoaded,
  };
}
